import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDataStudioColumnDetails,
  updateDataStudioColumnDetails,
} from "../services/dataStudio/dataStudioColumnDetailsApi";
import type {
  GetDataStudioColumnDetailsResponse,
  UpdateDataStudioColumnDetailsRequest,
} from "../services/dataStudio/dataStudioColumnDetails.types";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { ConnectorColumnIdentity } from "../services/dataStudio/dataStudioAssets.types";

/**
 * Column detail plus its curation mutation.
 *
 * The PATCH returns the full updated column, so the successful response is
 * written straight into this query's cache — no refetch, and the modal's form
 * rehydrates from the server's own view of the row.
 */
export const useDataStudioColumnDetails = (
  identity: ConnectorColumnIdentity,
  enabled: boolean,
) => {
  const { connectionId, assetId, columnId } = identity;
  const queryClient = useQueryClient();
  const queryKey = queryKeys.dataStudio.columnDetails(
    connectionId,
    assetId,
    columnId,
  );

  const query = useQuery<GetDataStudioColumnDetailsResponse>({
    queryKey,
    queryFn: () => getDataStudioColumnDetails(identity),
    enabled:
      enabled && Boolean(connectionId) && Boolean(assetId) && Boolean(columnId),
  });

  const mutation = useMutation({
    mutationFn: updateDataStudioColumnDetails,
    onSuccess: (response) => queryClient.setQueryData(queryKey, response),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    loadErrorMessage: query.isError
      ? getApiErrorMessage(query.error, "Unable to load column details.")
      : null,
    refetch: query.refetch,
    update: (
      request: UpdateDataStudioColumnDetailsRequest,
      onSuccess?: (response: GetDataStudioColumnDetailsResponse) => void,
    ) => mutation.mutate(request, { onSuccess }),
    isUpdating: mutation.isPending,
    updateErrorMessage: mutation.isError
      ? getApiErrorMessage(mutation.error, "Unable to update column details.")
      : null,
    resetUpdate: mutation.reset,
  };
};
